/**
 * AXEL Signal Journal — auto-tracked outcomes for every MT5 setup AXEL produces.
 *
 * Per-user view by default; admins see all signals with `mine_only=false`.
 * Updates from the backend 5-min outcome tracker show up on refresh.
 */
import React, { useEffect, useState } from "react";

const T = {
  bg: "#07090f", surf: "#0d1117", border: "#1c232f",
  white: "#e6e7ec", muted: "#878a91", gold: "#F5A623",
  green: "#22c55e", red: "#ef4444", blue: "#3b82f6", amber: "#f59e0b",
};
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  ACTIVE:      { color: T.blue,  label: "PENDING" },
  IN_TRADE:    { color: T.amber, label: "IN TRADE" },
  TP1_HIT:     { color: T.green, label: "TP1 HIT" },
  TP2_HIT:     { color: T.green, label: "TP2 HIT" },
  TP3_HIT:     { color: T.green, label: "TP3 HIT" },
  PARTIAL_TP:  { color: T.gold,  label: "PARTIAL TP" },
  SL_HIT:      { color: T.red,   label: "SL HIT" },
  EXPIRED:     { color: T.muted, label: "EXPIRED" },
  INVALIDATED: { color: T.muted, label: "INVALIDATED" },
};

// Options an admin can set manually (value → menu label).
const ADMIN_STATUS_OPTIONS = [
  ["ACTIVE",     "↺ Reset to Pending"],
  ["IN_TRADE",   "In Trade"],
  ["TP1_HIT",    "TP1 Hit"],
  ["TP2_HIT",    "TP2 Hit"],
  ["TP3_HIT",    "TP3 Hit"],
  ["PARTIAL_TP", "Partial Take Profit"],
  ["SL_HIT",     "SL Hit"],
  ["EXPIRED",    "Expired"],
];

const fmtPrice = (asset, v) => {
  if (v == null) return "—";
  const digits = asset === "BTCUSD" ? 0 : asset?.endsWith("JPY") ? 2 : asset === "XAUUSD" ? 2 : 4;
  return Number(v).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
};
const relTime = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

export default function SignalJournalTab({ isAdmin }) {
  const [signals, setSignals] = useState([]);
  const [stats, setStats]     = useState(null);
  const [scope, setScope]     = useState(isAdmin ? "all" : "mine");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [sourceFilter, setSourceFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey(k => k + 1);
  // signalId currently awaiting a "pips won" entry before committing PARTIAL_TP
  const [partialFor, setPartialFor] = useState(null);
  const [partialPips, setPartialPips] = useState("");

  useEffect(() => {
    let cancelled = false;
    const fetchJournal = async () => {
      setLoading(true);
      try {
        const mineOnly = scope === "mine" ? "true" : "false";
        const [sRes, statsRes] = await Promise.all([
          fetch(`${API}/axel/signals?mine_only=${mineOnly}&limit=200`, { credentials: "include" }),
          fetch(`${API}/axel/signals/stats?days=30&mine_only=${mineOnly}`, { credentials: "include" }),
        ]);
        if (cancelled) return;
        if (sRes.ok)     setSignals((await sRes.json()).signals || []);
        if (statsRes.ok) setStats(await statsRes.json());
      } catch { /* surfaced via empty list */ }
      if (!cancelled) setLoading(false);
    };
    fetchJournal();
    return () => { cancelled = true; };
  }, [scope, reloadKey]);

  const filtered = signals.filter(s =>
    (statusFilter === "ALL" || s.status === statusFilter) &&
    (sourceFilter === "ALL" || (s.source || "mt5_setup") === sourceFilter)
  );

  // Admin-only manual outcome labelling. Updates the row + stats in place so
  // both the Journal and Performance Card reflect it immediately.
  const setStatus = async (signalId, newStatus, pipsWon = null) => {
    try {
      const token = localStorage.getItem("flexx_access_token");
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const payload = { status: newStatus };
      if (newStatus === "PARTIAL_TP") payload.pips_won = Number(pipsWon) || 0;
      const r = await fetch(`${API}/axel/signals/${signalId}/status`, {
        method: "POST", credentials: "include", headers,
        body: JSON.stringify(payload),
      });
      if (!r.ok) return;
      const d = await r.json();
      if (d.signal) setSignals(prev => prev.map(s => (s.id === signalId ? d.signal : s)));
      // refresh stats silently (no table flicker)
      const mineOnly = scope === "mine" ? "true" : "false";
      const sr = await fetch(`${API}/axel/signals/stats?days=30&mine_only=${mineOnly}`, { credentials: "include" });
      if (sr.ok) setStats(await sr.json());
    } catch { /* no-op — row stays as-is */ }
  };

  // Admin picked a status from the row dropdown. PARTIAL_TP needs a pips value
  // first, so open the inline input instead of committing immediately.
  const onStatusPick = (signalId, value) => {
    if (!value) return;
    if (value === "PARTIAL_TP") {
      setPartialFor(signalId);
      setPartialPips("");
    } else {
      setStatus(signalId, value);
    }
  };

  const commitPartial = (signalId) => {
    if (partialPips === "" || isNaN(Number(partialPips))) return;
    setStatus(signalId, "PARTIAL_TP", Number(partialPips));
    setPartialFor(null);
    setPartialPips("");
  };

  const g = stats?.global || {};

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "8px 18px 40px" }} data-testid="signal-journal-tab">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 14 }}>
        <div>
          <h2 style={{ fontFamily: "Barlow Condensed, sans-serif", fontSize: "1.35rem", fontWeight: 800, color: T.white, margin: 0 }}>
            AXEL Signal Journal
          </h2>
          <div style={{ fontSize: ".62rem", color: T.muted, marginTop: 2, fontFamily: "Barlow, sans-serif" }}>
            Every Research Desk MT5 setup is auto-tracked from issue to TP / SL / expiry.
          </div>
        </div>
        {isAdmin && (
          <div style={{ display: "flex", gap: 6 }}>
            {[["mine", "MINE"], ["all", "ALL USERS"]].map(([id, label]) => (
              <button key={id} data-testid={`scope-${id}`} onClick={() => setScope(id)}
                style={{ padding: "5px 11px", borderRadius: 4, cursor: "pointer",
                  background: scope === id ? "#1a1407" : T.bg,
                  border: `1px solid ${scope === id ? T.gold : T.border}`,
                  color: scope === id ? T.gold : T.muted,
                  fontFamily: "Barlow Condensed, sans-serif", fontSize: ".7rem",
                  fontWeight: 700, letterSpacing: ".08em" }}>{label}</button>
            ))}
          </div>
        )}
      </div>

      {/* 30-day hit rate summary */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))",
                    gap: 10, marginBottom: 16 }}>
        {[
          ["Total (30d)",   g.total ?? 0,        T.white],
          ["Active",        g.active ?? 0,       T.amber],
          ["TP Hit %",      `${g.tp1_hit_pct ?? 0}%`, T.green],
          ["TP2 Hit %",     `${g.tp2_hit_pct ?? 0}%`, T.green],
          ["TP3 Hit %",     `${g.tp3_hit_pct ?? 0}%`, T.green],
          ["SL Hit %",      `${g.sl_hit_pct ?? 0}%`,  T.red],
          ["Total Pips",    `${(g.total_pips ?? 0) > 0 ? "+" : ""}${g.total_pips ?? 0}`, (g.total_pips ?? 0) >= 0 ? T.green : T.red],
          ["Expired",       g.expired ?? 0,      T.muted],
        ].map(([k, v, color]) => (
          <div key={k} style={{ background: T.surf, border: `1px solid ${T.border}`, borderRadius: 6, padding: "9px 12px" }}>
            <div style={{ fontSize: ".55rem", color: T.muted, letterSpacing: ".14em", textTransform: "uppercase",
                          fontFamily: "Barlow Condensed, sans-serif", marginBottom: 4 }}>{k}</div>
            <div data-testid={`stat-${k.replace(/[^a-z]/gi, "").toLowerCase()}`}
                 style={{ fontSize: "1.05rem", fontWeight: 800, color, fontFamily: "IBM Plex Mono, monospace" }}>
              {v}
            </div>
          </div>
        ))}
      </div>

      {/* Status filter pills */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        {["ALL", "ACTIVE", "IN_TRADE", "TP1_HIT", "TP2_HIT", "TP3_HIT", "PARTIAL_TP", "SL_HIT", "EXPIRED"].map(s => (
          <button key={s} data-testid={`filter-${s}`} onClick={() => setStatusFilter(s)}
            style={{ padding: "4px 9px", borderRadius: 4, cursor: "pointer",
              background: statusFilter === s ? "#1a1407" : T.bg,
              border: `1px solid ${statusFilter === s ? T.gold : T.border}`,
              color: statusFilter === s ? T.gold : T.muted,
              fontFamily: "Barlow Condensed, sans-serif", fontSize: ".62rem",
              fontWeight: 700, letterSpacing: ".08em" }}>{s.replace("_", " ")}</button>
        ))}
        <button data-testid="signal-refresh" onClick={reload} style={{
          padding: "4px 9px", borderRadius: 4, cursor: "pointer",
          background: T.bg, border: `1px solid ${T.border}`, color: T.muted,
          marginLeft: "auto", fontSize: ".62rem", fontFamily: "Barlow Condensed, sans-serif",
        }}>↻ REFRESH</button>
      </div>

      {/* Source filter — MT5 Setup vs Live Trigger */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: ".55rem", color: T.muted, letterSpacing: ".1em", textTransform: "uppercase",
                       fontFamily: "Barlow Condensed, sans-serif", marginRight: 2 }}>Engine:</span>
        {[["ALL", "ALL"], ["mt5_setup", "📐 MT5 SETUP"], ["live_trigger", "⚡ LIVE TRIGGER"]].map(([id, label]) => (
          <button key={id} data-testid={`source-filter-${id}`} onClick={() => setSourceFilter(id)}
            style={{ padding: "4px 9px", borderRadius: 4, cursor: "pointer",
              background: sourceFilter === id ? "#1a1407" : T.bg,
              border: `1px solid ${sourceFilter === id ? T.gold : T.border}`,
              color: sourceFilter === id ? T.gold : T.muted,
              fontFamily: "Barlow Condensed, sans-serif", fontSize: ".62rem",
              fontWeight: 700, letterSpacing: ".08em" }}>{label}</button>
        ))}
        {stats?.per_source && Object.keys(stats.per_source).length > 0 && (
          <span data-testid="journal-source-compare" style={{ marginLeft: "auto", fontSize: ".6rem", color: T.muted,
                        fontFamily: "Barlow, sans-serif" }}>
            {["mt5_setup", "live_trigger"].filter(k => stats.per_source[k]).map(k => (
              <span key={k} style={{ marginLeft: 12 }}>
                {k === "live_trigger" ? "⚡ LT" : "📐 MT5"}: <b style={{ color: T.green }}>{stats.per_source[k].tp1_hit_pct ?? 0}%</b> TP1 ({stats.per_source[k].total} issued)
              </span>
            ))}
          </span>
        )}
      </div>

      {/* Signal table */}
      {loading ? (
        <div style={{ color: T.muted, padding: 24, textAlign: "center", fontSize: ".72rem" }}>
          Loading signals…
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ color: T.muted, padding: 24, textAlign: "center", fontSize: ".72rem",
                      border: `1px dashed ${T.border}`, borderRadius: 6, fontFamily: "Barlow, sans-serif" }}>
          No signals yet. Run an MT5 setup in Research Desk — AXEL signals appear here automatically.
        </div>
      ) : (
        <div style={{ background: T.surf, border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
          <div style={{ display: "grid",
                        gridTemplateColumns: scope === "all" ? "60px 70px 60px 110px 80px 90px 90px 132px 1fr"
                                                             : "60px 70px 60px 110px 80px 90px 90px 132px",
                        background: T.bg, padding: "8px 10px",
                        fontSize: ".55rem", color: T.muted, letterSpacing: ".12em",
                        textTransform: "uppercase", fontFamily: "Barlow Condensed, sans-serif" }}>
            <div>Time</div><div>Asset</div><div>Side</div><div>Entry</div>
            <div>SL</div><div>TP1 / TP2</div><div>Excursion</div><div>Status</div>
            {scope === "all" && <div>User</div>}
          </div>
          {filtered.map(s => {
            const stat = STATUS_STYLE[s.status] || { color: T.muted, label: s.status };
            return (
              <div key={s.id} data-testid={`signal-row-${s.id}`}
                style={{ display: "grid",
                  gridTemplateColumns: scope === "all" ? "60px 70px 60px 110px 80px 90px 90px 132px 1fr"
                                                       : "60px 70px 60px 110px 80px 90px 90px 132px",
                  padding: "10px",
                  borderTop: `1px solid ${T.border}`,
                  fontSize: ".7rem", color: T.white,
                  fontFamily: "IBM Plex Mono, monospace" }}>
                <div title={s.issued_at} style={{ color: T.muted }}>{relTime(s.issued_at)}</div>
                <div style={{ color: T.gold, fontWeight: 700 }}>
                  {s.asset}
                  <div data-testid={`source-tag-${s.id}`} title={(s.source || "mt5_setup") === "live_trigger" ? "Live Trigger" : "MT5 Setup"}
                    style={{ fontSize: ".5rem", fontWeight: 800, letterSpacing: ".04em", marginTop: 2,
                      fontFamily: "Barlow Condensed, sans-serif",
                      color: (s.source || "mt5_setup") === "live_trigger" ? "#F5A623" : "#38bdf8" }}>
                    {(s.source || "mt5_setup") === "live_trigger" ? "⚡ LT" : "📐 MT5"}
                  </div>
                </div>
                <div style={{ color: s.direction === "BUY" ? T.green : T.red, fontWeight: 700 }}>
                  {s.direction}
                </div>
                <div>{fmtPrice(s.asset, s.entry_low)}–{fmtPrice(s.asset, s.entry_high)}</div>
                <div style={{ color: T.red }}>{fmtPrice(s.asset, s.stop_loss)}</div>
                <div style={{ color: T.green }}>
                  {fmtPrice(s.asset, s.tp1)}{s.tp2 ? ` / ${fmtPrice(s.asset, s.tp2)}` : ""}
                </div>
                <div title={`Favorable: ${s.max_favorable_pips?.toFixed(0) || 0} pips · Adverse: ${s.max_adverse_pips?.toFixed(0) || 0} pips`}>
                  <span style={{ color: T.green }}>+{Math.round(s.max_favorable_pips || 0)}</span>
                  <span style={{ color: T.muted }}> / </span>
                  <span style={{ color: T.red }}>-{Math.round(s.max_adverse_pips || 0)}</span>
                </div>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                    <span style={{
                      background: stat.color + "22", color: stat.color,
                      padding: "2px 6px", borderRadius: 3,
                      fontSize: ".55rem", fontFamily: "Barlow Condensed, sans-serif",
                      fontWeight: 800, letterSpacing: ".08em",
                    }}>{stat.label}</span>
                    {s.manual_override && (
                      <span data-testid={`manual-tag-${s.id}`} title={`Manually set${s.manual_override_by ? ` by ${s.manual_override_by}` : ""}`}
                        style={{ background: T.gold + "22", color: T.gold, padding: "1px 4px",
                          borderRadius: 3, fontSize: ".48rem", fontFamily: "Barlow Condensed, sans-serif",
                          fontWeight: 800, letterSpacing: ".06em" }}>🔒 MANUAL</span>
                    )}
                    {s.status === "PARTIAL_TP" && s.pips_won != null && (
                      <span data-testid={`pips-won-${s.id}`}
                        title="Pips banked on this partial / early exit"
                        style={{ background: (s.pips_won >= 0 ? T.green : T.red) + "22",
                          color: s.pips_won >= 0 ? T.green : T.red, padding: "1px 5px",
                          borderRadius: 3, fontSize: ".52rem", fontFamily: "IBM Plex Mono, monospace",
                          fontWeight: 800 }}>
                        {s.pips_won >= 0 ? "+" : ""}{s.pips_won} pips
                      </span>
                    )}
                  </div>
                  {isAdmin && partialFor === s.id ? (
                    <div style={{ marginTop: 5, display: "flex", gap: 4, alignItems: "center" }}>
                      <input data-testid={`pips-input-${s.id}`} type="number" autoFocus
                        value={partialPips} placeholder="pips ±"
                        onChange={(e) => setPartialPips(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") commitPartial(s.id); }}
                        style={{ width: 62, background: T.bg, color: T.white,
                          border: `1px solid ${T.gold}`, borderRadius: 4,
                          fontSize: ".6rem", fontFamily: "IBM Plex Mono, monospace",
                          padding: "3px 5px" }} />
                      <button data-testid={`pips-confirm-${s.id}`} onClick={() => commitPartial(s.id)}
                        style={{ background: T.gold, color: "#07090f", border: "none",
                          borderRadius: 4, fontSize: ".6rem", fontWeight: 800, cursor: "pointer",
                          padding: "3px 7px", fontFamily: "Barlow Condensed, sans-serif" }}>✓</button>
                      <button data-testid={`pips-cancel-${s.id}`} onClick={() => { setPartialFor(null); setPartialPips(""); }}
                        style={{ background: T.bg, color: T.muted, border: `1px solid ${T.border}`,
                          borderRadius: 4, fontSize: ".6rem", cursor: "pointer",
                          padding: "3px 6px", fontFamily: "Barlow Condensed, sans-serif" }}>✕</button>
                    </div>
                  ) : isAdmin && (
                    <select data-testid={`status-select-${s.id}`}
                      value=""
                      onChange={(e) => onStatusPick(s.id, e.target.value)}
                      style={{ marginTop: 5, width: "100%", maxWidth: 128, background: T.bg,
                        color: T.muted, border: `1px solid ${T.border}`, borderRadius: 4,
                        fontSize: ".55rem", fontFamily: "Barlow Condensed, sans-serif",
                        padding: "3px 4px", cursor: "pointer" }}>
                      <option value="">Set status…</option>
                      {ADMIN_STATUS_OPTIONS.map(([val, label]) => (
                        <option key={val} value={val}>{label}</option>
                      ))}
                    </select>
                  )}
                </div>
                {scope === "all" && (
                  <div style={{ color: T.muted, fontFamily: "Barlow, sans-serif", fontSize: ".65rem",
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                       title={s.user_email}>{s.user_email || "—"}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
